import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Search, Package, Truck, Clock, Check, ChevronRight,
  FileText, MapPin, DollarSign, AlertTriangle, ThumbsUp, ThumbsDown, MessageSquare, Star, Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
  order_placed: "Order Placed",
  proof_sent: "Proof Sent",
  proof_approved: "Proof Approved",
  in_production: "In Production",
  quality_check: "Quality Check",
  ready_to_ship: "Ready to Ship",
  shipped: "Shipped",
  on_hold: "On Hold",
  blocked: "Blocked",
};

const PROGRESS_STAGES = ["order_placed", "proof_sent", "proof_approved", "in_production", "quality_check", "ready_to_ship", "shipped"];

const STATUS_STYLE = {
  order_placed: "bg-blue-50 text-blue-600 border-blue-200",
  proof_sent: "bg-violet-50 text-violet-600 border-violet-200",
  proof_approved: "bg-cyan-50 text-cyan-600 border-cyan-200",
  in_production: "bg-amber-50 text-amber-600 border-amber-200",
  quality_check: "bg-emerald-50 text-emerald-600 border-emerald-200",
  ready_to_ship: "bg-teal-50 text-teal-600 border-teal-200",
  shipped: "bg-indigo-50 text-indigo-600 border-indigo-200",
  on_hold: "bg-red-50 text-red-600 border-red-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
};

export default function CustomerPortal() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [ordersList, setOrdersList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [proofFeedback, setProofFeedback] = useState("");
  const [proofActionDone, setProofActionDone] = useState(false);
  const [proofMessage, setProofMessage] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [reviewDone, setReviewDone] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportForm, setSupportForm] = useState({ name: "", email: "", phone: "", order_number: "", details: "", preferred_contact: "email" });
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() && !email.trim()) {
      setError("Please enter an order number or email address.");
      return;
    }
    setError("");
    setOrder(null);
    setOrdersList(null);
    setProofActionDone(false);
    setProofMessage("");
    setShowReview(false);
    setReviewDone(false);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/customer/lookup`, {
        order_number: orderNumber.trim(),
        email: email.trim(),
      });
      if (data.multiple) {
        setOrdersList(data.orders);
      } else {
        setOrder(data.order);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "No orders found. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const selectOrder = (o) => {
    setOrder(o);
    setOrdersList(null);
    setOrderNumber(o.order_number);
    setEmail(o.customer_email || email);
  };

  const handleProofAction = async (approved) => {
    try {
      const { data } = await axios.post(`${API}/customer/approve-proof`, {
        order_number: orderNumber,
        email,
        approved,
        feedback: proofFeedback,
      });
      setProofActionDone(true);
      setProofMessage(data.message);
      const { data: updated } = await axios.post(`${API}/customer/lookup`, {
        order_number: orderNumber,
        email,
      });
      if (!updated.multiple) setOrder(updated.order);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit feedback");
    }
  };

  const GOOGLE_REVIEW_URL = "https://g.page/r/CfrO2avGt2MrEAE/review";
  
  const handleReviewSubmit = async () => {
    if (reviewRating === 0) return;
    
    // 5-star: redirect to Google immediately (capture the review there, not locally)
    if (reviewRating === 5) {
      try {
        await axios.post(`${API}/reviews/submit`, {
          order_number: orderNumber,
          email: email || order?.customer_email || "",
          rating: 5,
          feedback: "Directed to Google Review",
        });
      } catch (err) { /* still redirect */ }
      setReviewDone(true);
      setReviewResult({ redirect_to_google: true, message: "Thank you! Taking you to Google Reviews..." });
      setTimeout(() => {
        window.open(GOOGLE_REVIEW_URL, "_blank");
      }, 1500);
      return;
    }
    
    // <5 stars: collect feedback and send to admin
    try {
      const { data } = await axios.post(`${API}/reviews/submit`, {
        order_number: orderNumber,
        email: email || order?.customer_email || "",
        rating: reviewRating,
        feedback: reviewFeedback,
      });
      setReviewDone(true);
      setReviewResult(data);
    } catch (err) {
      console.error("Review failed:", err);
    }
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    if (!supportForm.name || !supportForm.email || !supportForm.details) return;
    setSupportLoading(true);
    try {
      const payload = {
        ...supportForm,
        order_number: supportForm.order_number || order?.order_number || "",
      };
      const { data } = await axios.post(`${API}/customer/support`, payload);
      setSupportSubmitted(true);
      setSupportMessage(data.message);
    } catch (err) {
      setSupportMessage(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSupportLoading(false);
    }
  };

  const currentIdx = order ? PROGRESS_STAGES.indexOf(order.status) : -1;
  const isHeld = order?.status === "on_hold" || order?.status === "blocked";

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-portal">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl mb-4 shadow-lg shadow-orange-500/20">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Your Order</h1>
          <p className="text-gray-500 max-w-md mx-auto">
            Enter your order number, email, or both to look up your order.
          </p>
        </div>

        <Card className="mb-8 border-gray-200 shadow-sm" data-testid="order-lookup-card">
          <CardContent className="py-6">
            <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label className="text-gray-600 text-xs mb-1">Order Number</Label>
                <Input
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="FPB-001"
                  className="bg-white border-gray-300"
                  data-testid="portal-order-number"
                />
              </div>
              <div className="flex items-end text-gray-400 text-sm font-medium pb-2 px-1">or</div>
              <div className="flex-1">
                <Label className="text-gray-600 text-xs mb-1">Email Address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-white border-gray-300"
                  data-testid="portal-email"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto px-8"
                  data-testid="portal-lookup-btn"
                >
                  {loading ? "Searching..." : <><Search className="w-4 h-4 mr-2" /> Track</>}
                </Button>
              </div>
            </form>
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="portal-error">
                {error}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Multiple orders list (email search) */}
        {ordersList && (
          <div className="space-y-3 mb-8" data-testid="orders-list">
            <h3 className="text-lg font-semibold text-gray-900">Your Orders</h3>
            {ordersList.map((o) => (
              <div
                key={o.order_number}
                onClick={() => selectOrder(o)}
                className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-orange-300 hover:shadow-sm transition flex items-center justify-between"
                data-testid={`order-select-${o.order_number}`}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-orange-600">{o.order_number}</span>
                    <Badge className={`text-[10px] border ${STATUS_STYLE[o.status]}`}>
                      {STAGE_LABELS[o.status] || o.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">
                    {o.items?.map(i => i.name).join(", ")} &middot; {new Date(o.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900">${o.total_amount?.toLocaleString()}</div>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Single order detail */}
        {order && (
          <div className="space-y-6 animate-in fade-in-0 duration-300" data-testid="portal-order-detail">
            {ordersList === null && (
              <button onClick={() => { setOrder(null); setOrdersList(null); }} className="text-sm text-gray-500 hover:text-gray-700">&larr; New search</button>
            )}

            <Card className="border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-white text-xl font-bold">{order.order_number}</h2>
                    <p className="text-orange-100 text-sm">Hi {order.customer_name}</p>
                  </div>
                  <Badge className={`${STATUS_STYLE[order.status]} border text-sm px-3 py-1`}>
                    {STAGE_LABELS[order.status] || order.status}
                  </Badge>
                </div>
              </div>

              <CardContent className="py-5 overflow-x-auto">
                <div className="flex items-center min-w-[600px]">
                  {PROGRESS_STAGES.map((stage, idx) => {
                    const done = idx <= currentIdx && !isHeld;
                    const current = idx === currentIdx && !isHeld;
                    return (
                      <div key={stage} className="flex items-center flex-1">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${
                            current ? "bg-orange-500 text-white ring-4 ring-orange-100" : done ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"
                          }`}>
                            {done && !current ? <Check className="w-4 h-4" /> : idx + 1}
                          </div>
                          <span className={`text-[10px] mt-1 text-center w-16 ${current ? "text-orange-600 font-semibold" : done ? "text-green-600" : "text-gray-400"}`}>
                            {STAGE_LABELS[stage]}
                          </span>
                        </div>
                        {idx < PROGRESS_STAGES.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-1 ${idx < currentIdx ? "bg-green-500" : "bg-gray-200"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {isHeld && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2" data-testid="hold-notice">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-700 font-medium text-sm">Order {order.status === "on_hold" ? "On Hold" : "Blocked"}</p>
                      {order.hold_reason && <p className="text-red-600 text-sm mt-1">{order.hold_reason}</p>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Items */}
                <Card className="border-gray-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-gray-900 flex items-center gap-2"><Package className="w-4 h-4 text-orange-500" /> Order Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {order.items?.map((item, i) => (
                        <div key={item.name ? `item-${item.name}-${i}` : `item-${i}`} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                          <div className="flex justify-between mb-2">
                            <span className="font-medium text-gray-900">{item.name}</span>
                            <span className="text-gray-500 text-sm">Qty: {item.quantity}</span>
                          </div>
                          {item.sizes && Object.keys(item.sizes).length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {Object.entries(item.sizes).map(([size, qty]) => (
                                <span key={size} className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{size}: {qty}</span>
                              ))}
                            </div>
                          )}
                          {item.colors?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {item.colors.map((c) => (
                                <span key={c} className="text-xs bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full">{c}</span>
                              ))}
                            </div>
                          )}
                          {item.print_locations?.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <MapPin className="w-3 h-3" /> {item.print_locations.map(l => l.replace(/_/g, " ")).join(", ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Proof review */}
                {order.proof_files?.length > 0 && (
                  <Card className="border-gray-200 shadow-sm border-l-4 border-l-violet-500" data-testid="proof-review-section">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4 text-violet-500" /> Proof Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-4">
                        {order.proof_files.map((f) => (
                          <div key={f.id} className="bg-violet-50 rounded-lg p-3 border border-violet-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-violet-500" />
                              <span className="text-sm text-gray-700">{f.original_filename}</span>
                            </div>
                            <span className="text-xs text-gray-500">{new Date(f.uploaded_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>

                      {proofActionDone ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="proof-action-done">
                          <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                          <p className="text-green-700 font-medium">{proofMessage}</p>
                        </div>
                      ) : order.proof_status === "approved" ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                          <p className="text-green-700 font-medium">Proof approved! Your order is in production.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">Review the proof files above. Approve to move your order to production.</p>
                          <Textarea value={proofFeedback} onChange={(e) => setProofFeedback(e.target.value)} placeholder="Any changes needed?" className="bg-white border-gray-300" rows={2} data-testid="proof-feedback" />
                          <div className="flex gap-3">
                            <Button onClick={() => handleProofAction(true)} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="approve-proof-btn">
                              <ThumbsUp className="w-4 h-4 mr-2" /> Approve
                            </Button>
                            <Button onClick={() => handleProofAction(false)} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="request-revision-btn">
                              <ThumbsDown className="w-4 h-4 mr-2" /> Request Revision
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Review section - show for shipped orders */}
                {order.status === "shipped" && (
                  <Card className="border-gray-200 shadow-sm border-l-4 border-l-amber-500" data-testid="review-section">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-gray-900 flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500" /> How was your experience?
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {reviewDone ? (
                        <div className="text-center py-4" data-testid="review-done">
                          <div className="text-4xl mb-2">{"*".repeat(reviewRating).split("").map((_, i) => <Star key={`star-${i}`} className="w-6 h-6 inline text-amber-400 fill-amber-400" />)}</div>
                          <p className="text-gray-700 font-medium">{reviewResult?.message}</p>
                          {reviewResult?.redirect_to_google && (
                            <div className="mt-3 space-y-2">
                              <p className="text-sm text-gray-500">A new tab should open with Google Reviews.</p>
                              <Button onClick={() => window.open(GOOGLE_REVIEW_URL, "_blank")} variant="outline" className="border-orange-500 text-orange-600" data-testid="google-review-link">
                                Open Google Reviews
                              </Button>
                            </div>
                          )}
                          {!reviewResult?.redirect_to_google && (
                            <p className="text-sm text-green-600 mt-2">Your feedback has been sent to our team. We'll work on improving!</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-center gap-2" data-testid="star-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setReviewRating(star)}
                                className="p-1 transition-transform hover:scale-110"
                                data-testid={`star-${star}`}
                              >
                                <Star className={`w-8 h-8 ${star <= reviewRating ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} />
                              </button>
                            ))}
                          </div>
                          {reviewRating > 0 && reviewRating < 5 && (
                            <Textarea
                              value={reviewFeedback}
                              onChange={(e) => setReviewFeedback(e.target.value)}
                              placeholder="What could we improve?"
                              className="bg-white border-gray-300"
                              rows={3}
                              data-testid="review-feedback"
                            />
                          )}
                          {reviewRating > 0 && (
                            <Button onClick={handleReviewSubmit} className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="submit-review-btn">
                              {reviewRating === 5 ? "Leave a Google Review" : "Submit Feedback"}
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Timeline */}
                <Card className="border-gray-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-gray-900 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {order.status_history?.slice().reverse().map((h, i) => (
                        <div key={h.timestamp ? `hist-${h.timestamp}` : `hist-${i}`} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${i === 0 ? "bg-orange-500" : "bg-gray-300"}`} />
                            {i < order.status_history.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                          </div>
                          <div className="pb-4">
                            <p className="text-sm font-medium text-gray-900 capitalize">{STAGE_LABELS[h.status] || h.status?.replace(/_/g, " ")}</p>
                            <p className="text-xs text-gray-500">{new Date(h.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right sidebar */}
              <div className="space-y-6">
                <Card className="border-gray-200 shadow-sm">
                  <CardContent className="py-5 text-center">
                    <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-gray-900">${order.total_amount?.toLocaleString()}</div>
                    <Badge className={`mt-2 ${
                      order.payment_status === "paid" ? "bg-green-100 text-green-700" :
                      order.payment_status === "partial" ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {order.payment_status === "paid" ? "Paid" : order.payment_status === "partial" ? "Partially Paid" : "Payment Pending"}
                    </Badge>
                  </CardContent>
                </Card>

                {order.tracking_number && (
                  <Card className="border-gray-200 shadow-sm border-l-4 border-l-indigo-500" data-testid="tracking-card">
                    <CardContent className="py-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-5 h-5 text-indigo-500" />
                        <span className="font-semibold text-gray-900">Shipped!</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">Tracking Number:</p>
                      <p className="font-mono text-sm bg-gray-100 rounded px-3 py-2 text-gray-900 break-all" data-testid="tracking-number">
                        {order.tracking_number}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {order.due_date && (
                  <Card className="border-gray-200 shadow-sm">
                    <CardContent className="py-5 text-center">
                      <Clock className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Estimated Completion</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">
                        {new Date(order.due_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-gray-200 shadow-sm" data-testid="support-section">
                  <CardContent className="py-5">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-orange-500" /> Need Help?
                    </h3>

                    {supportSubmitted ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="support-submitted">
                        <Check className="w-6 h-6 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 text-sm font-medium">{supportMessage}</p>
                      </div>
                    ) : !showSupportForm ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-3">Questions about your order? We're here to help.</p>
                        <Button
                          onClick={() => {
                            setShowSupportForm(true);
                            setSupportForm(f => ({
                              ...f,
                              email: email || order?.customer_email || "",
                              name: order?.customer_name || "",
                              order_number: order?.order_number || orderNumber || "",
                            }));
                          }}
                          className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white"
                          data-testid="contact-support-btn"
                        >
                          <Send className="w-4 h-4 mr-2" /> Contact Support
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSupportSubmit} className="space-y-3" data-testid="support-form">
                        <div>
                          <Label className="text-gray-600 text-xs">Your Name *</Label>
                          <Input value={supportForm.name} onChange={(e) => setSupportForm({...supportForm, name: e.target.value})} className="bg-white border-gray-300" required data-testid="support-name" />
                        </div>
                        <div>
                          <Label className="text-gray-600 text-xs">Email *</Label>
                          <Input type="email" value={supportForm.email} onChange={(e) => setSupportForm({...supportForm, email: e.target.value})} className="bg-white border-gray-300" required data-testid="support-email" />
                        </div>
                        <div>
                          <Label className="text-gray-600 text-xs">Phone</Label>
                          <Input value={supportForm.phone} onChange={(e) => setSupportForm({...supportForm, phone: e.target.value})} className="bg-white border-gray-300" placeholder="Optional" data-testid="support-phone" />
                        </div>
                        <div>
                          <Label className="text-gray-600 text-xs">Order # (if applicable)</Label>
                          <Input value={supportForm.order_number} onChange={(e) => setSupportForm({...supportForm, order_number: e.target.value})} className="bg-white border-gray-300" placeholder="FPB-001" data-testid="support-order" />
                        </div>
                        <div>
                          <Label className="text-gray-600 text-xs">How can we help? *</Label>
                          <Textarea value={supportForm.details} onChange={(e) => setSupportForm({...supportForm, details: e.target.value})} className="bg-white border-gray-300" rows={3} placeholder="Describe your issue..." required data-testid="support-details" />
                        </div>
                        <div>
                          <Label className="text-gray-600 text-xs">Preferred Contact Method</Label>
                          <select value={supportForm.preferred_contact} onChange={(e) => setSupportForm({...supportForm, preferred_contact: e.target.value})} className="w-full bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-2 text-sm mt-1" data-testid="support-contact-method">
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="either">Either</option>
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <Button type="submit" disabled={supportLoading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="submit-support-btn">
                            {supportLoading ? "Sending..." : "Submit"}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowSupportForm(false)} className="border-gray-300 text-gray-500">Cancel</Button>
                        </div>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
